import { Checkbox, DesktopView, GameInfo, StatsGame } from "best-common-react";
import React, { useEffect } from "react";
import styled from "styled-components";
import { getSurroundingGames } from "../../../api/RequesTixApi";
import { useAuth } from "../../../contexts/AuthContext";

const ButtonLink = styled.button.attrs({ className: "btn btn-link" })`
  font-size: 0.875rem;
  margin-left: 0.5rem;
`;

const SelectTabletText = styled.div`
  display: none;
  @media (min-width: 768px) and (max-width: 991px) {
    display: block;
  }
`;

type GameListProps = {
  gamePk: number;
  surroundingGames: StatsGame[];
  onChange: (value: string | number, checked: boolean) => void;
  selected: number[];
  canSelectGame?: boolean;
  onGameChange: (value: string) => void;
};

const GameList: React.FC<GameListProps> = ({
  gamePk,
  surroundingGames,
  onChange,
  selected,
  canSelectGame = false,
  onGameChange,
}) => (
  <div>
    {surroundingGames.map((game) => (
      <AdditionalGame
        game={game}
        isChecked={selected.includes(game.gamePk)}
        key={game.gamePk}
        onChange={onChange}
        canSelectGame={canSelectGame}
        disabled={gamePk === game.gamePk}
        onGameChange={onGameChange}
      />
    ))}
    {surroundingGames.length === 0 && <span>There are no surrounding games</span>}
  </div>
);

type AdditionalGameProps = {
  game: StatsGame;
  isChecked: boolean;
  onChange: (value: string | number, checked: boolean) => void;
  canSelectGame?: boolean;
  disabled: boolean;
  onGameChange: (value: string | number) => void;
};

const AdditionalGame: React.FC<AdditionalGameProps> = ({
  game,
  isChecked,
  onChange,
  canSelectGame = false,
  disabled,
  onGameChange,
}) => {
  const { isAdmin } = useAuth();
  return (
    <div className="d-flex align-items-center bcr-mt-1">
      <Checkbox
        id={`another-game-${game.gamePk}`}
        checked={isChecked}
        label={
          <GameInfo
            gameDate={game.gameDate}
            venue={game.venue}
            // tbd={game.status.startTimeTBD}
            home={game.teams.home.team}
            away={game.teams.away.team}
            seriesDescription={game.seriesDescription}
          />
        }
        onChange={() => onChange(game.gamePk, !isChecked)}
        gutterBottom
      />

      {isAdmin && canSelectGame && isChecked && (
        <>
          <DesktopView>
            <ButtonLink disabled={disabled} onClick={() => onGameChange(game.gamePk!)}>
              select this game
            </ButtonLink>
          </DesktopView>
          <SelectTabletText>
            <ButtonLink>select</ButtonLink>
          </SelectTabletText>
        </>
      )}
    </div>
  );
};

type TicketRequestSurroundingGamesProps = {
  originalGamePk?: number;
  gamePk: number;
  selected: number[];
  setSelected: (value: number[]) => void;
  canSelectGame?: boolean;
  onGameChange: (value: string) => void;
};

const TicketRequestSurroundingGames: React.FC<TicketRequestSurroundingGamesProps> = ({
  originalGamePk,
  gamePk,
  selected,
  setSelected,
  canSelectGame,
  onGameChange,
}) => {
  const [showSurrounding, setShowSurrounding] = React.useState<boolean>(false);
  const [surroundingGames, setSurroundingGames] = React.useState<StatsGame[]>([]);

  useEffect(() => {
    const pk: number = originalGamePk ? originalGamePk : gamePk;
    if (pk) {
      getSurroundingGames(pk).then(setSurroundingGames);
    }
  }, [originalGamePk, gamePk]);

  useEffect(() => {
    if (selected && selected.length > 0) {
      setShowSurrounding(true);
    }
  }, [selected]);

  const onChange = (gamePk: number, isChecked: boolean) => {
    if (isChecked) {
      setSelected([...selected, gamePk]);
    } else {
      setSelected(selected.filter((selected: number) => selected !== gamePk));
    }
  };

  return (
    <div>
      {surroundingGames && surroundingGames.length > 0 ? (
        <>
          <Checkbox
            id="showSurroundingGames"
            label="I would like another game in the surrounding days if this one is not available"
            checked={showSurrounding}
            onChange={() => setShowSurrounding(!showSurrounding)}
          />
          {showSurrounding ? (
            <GameList
              gamePk={gamePk}
              surroundingGames={surroundingGames}
              onChange={onChange}
              selected={selected}
              canSelectGame={canSelectGame}
              onGameChange={onGameChange}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default TicketRequestSurroundingGames;
